import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Loader2, Calendar, Info, TrendingDown, Palmtree
} from 'lucide-react';
import { cn } from '@/lib/utils';

export function LeaveBalanceTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [entitlement, setEntitlement] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userProfile?.id) {
      setLoading(false);
      return;
    }

    const loadBalance = async () => {
      try {
        setLoading(true);
        const currentYear = new Date().getFullYear();

        // Load entitlement for current year
        const { data: entitlementData, error: entitlementError } = await supabase
          .from('leave_entitlements')
          .select('*')
          .eq('user_id', userProfile.id)
          .eq('year', currentYear)
          .single();

        if (entitlementError && entitlementError.code !== 'PGRST116') {
          throw entitlementError;
        }

        // Load balance for current year
        const { data: balanceData, error: balanceError } = await supabase
          .from('leave_balances')
          .select('*')
          .eq('user_id', userProfile.id)
          .eq('year', currentYear)
          .single();

        if (balanceError && balanceError.code !== 'PGRST116') {
          // Non-missing row error
          console.error('Balance error:', balanceError);
        }

        setEntitlement(entitlementData || null);
        setBalance(balanceData || null);
      } catch (error) {
        console.error('Failed to load leave balance:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBalance();
  }, [userProfile?.id]);

  const goToProfile = () => {
    navigate('/attendance/entitlements');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!entitlement) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <Info className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No entitlement set</p>
        <p className="text-xs text-gray-500 mt-1">Contact your manager</p>
      </div>
    );
  }

  // Calculate totals
  const totalEntitled = entitlement.annual_leave_days + (entitlement.carry_over_days || 0);
  const takenDays = balance?.taken_days || 0;
  const remainingDays = balance?.remaining_days || totalEntitled;
  const usagePercent = totalEntitled > 0 ? Math.round((takenDays / totalEntitled) * 100) : 0;

  const isLowBalance = remainingDays <= 5;
  const currentYear = new Date().getFullYear();

  return (
    <TooltipProvider delayDuration={200}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <Badge variant="outline" className="text-xs">
            {currentYear}
          </Badge>
          {isLowBalance && remainingDays > 0 && (
            <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px]">
              <TrendingDown className="h-3 w-3 mr-1" />
              Low balance
            </Badge>
          )}
        </div>

        {/* Main stat */}
        <div className="flex-1 flex flex-col items-center justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-center cursor-pointer" onClick={goToProfile}>
                <div className={cn(
                  "text-4xl font-bold mb-1",
                  isLowBalance ? "text-orange-600" : "text-violet-600"
                )}>
                  {remainingDays}
                </div>
                <div className="text-sm text-gray-600">days remaining</div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="p-3 bg-white">
              <div className="space-y-2">
                <div className="font-semibold text-sm text-gray-900">Leave Balance Details</div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-gray-500">Annual Entitlement</p>
                    <p className="font-medium">{entitlement.annual_leave_days} days</p>
                  </div>
                  {entitlement.carry_over_days > 0 && (
                    <div>
                      <p className="text-gray-500">Carry Over</p>
                      <p className="font-medium">{entitlement.carry_over_days} days</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500">Total Entitled</p>
                    <p className="font-medium text-violet-600">{totalEntitled} days</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Taken</p>
                    <p className="font-medium text-orange-600">{takenDays} days</p>
                  </div>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>

          {/* Progress bar */}
          <div className="w-full mt-4 space-y-1">
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>Used: {takenDays}/{totalEntitled} days</span>
              <span>{usagePercent}%</span>
            </div>
            <Progress
              value={usagePercent}
              className={cn(
                "h-2",
                usagePercent > 80 && "[&>div]:bg-orange-500",
                usagePercent > 90 && "[&>div]:bg-red-500"
              )}
            />
          </div>

          {/* Quick stats */}
          <div className="w-full mt-3 pt-3 border-t grid grid-cols-2 gap-2 text-center">
            <div>
              <p className="text-lg font-semibold text-gray-900">{entitlement.annual_leave_days}</p>
              <p className="text-[10px] text-gray-500">Annual</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900">{entitlement.carry_over_days || 0}</p>
              <p className="text-[10px] text-gray-500">Carry Over</p>
            </div>
          </div>
        </div>

        {/* Footer - link to entitlements */}
        <button
          onClick={goToProfile}
          className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors"
        >
          <Palmtree className="h-3.5 w-3.5" />
          <span>View Full Details</span>
        </button>
      </div>
    </TooltipProvider>
  );
}
